import { useEffect, useReducer } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { DeployUpToDateSignal } from '@volley/crucible/core/index.js';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

/**
 * The exact React Query key `useDeployUpToDate` caches under. Exported
 * so callers that KNOW the answer changed out-of-band (a new local
 * commit landed, a deploy just succeeded) can invalidate it directly
 * — see the staleness fix below.
 */
export function deployUpToDateQueryKey(
  client: ReturnType<typeof useCrucibleClient>,
  gameId: string | undefined,
): readonly unknown[] {
  return projectQueryKey(client, ['deploy:check-up-to-date', gameId]);
}

/**
 * Bugbot round 2 — "Stale up-to-date blocks deploy": a positive
 * `upToDate: true` cached for up to 30s (or until the next window
 * focus) with NO invalidation after a new local commit or a
 * successful deploy would leave the Deploy button wrongly disabled
 * for that whole window after a real, same-session change — exactly
 * the false-block class this feature's fail-open contract exists to
 * prevent. Call this the moment such a change is known (a
 * `localGit.lastCommitDate` change, a `publish:run` success) so the
 * NEXT read is forced fresh instead of trusting the stale cache.
 */
export function invalidateDeployUpToDate(
  queryClient: QueryClient,
  client: ReturnType<typeof useCrucibleClient>,
  gameId: string | undefined,
): void {
  void queryClient.invalidateQueries({ queryKey: deployUpToDateQueryKey(client, gameId) });
}

/**
 * crucible#3495 — on-demand "already up to date on the TV" check for
 * the Save-to-TV readiness gate.
 *
 * Wraps `deploy:check-up-to-date` in a React Query cache:
 *  - `staleTime: 30s` — short-TTL, so repeated renders/mounts within
 *    a session stay cheap (the IPC handler shells out to `git fetch`).
 *  - `refetchOnWindowFocus: 'always'` — the product rail this exists
 *    for: re-check when the user alt-tabs back into the GUI, the
 *    canonical moment a deploy elsewhere (another checkout, a CI
 *    push) could have changed the answer. Mirrors
 *    `useTemplateUpdateCheck`'s identical "out-of-band change" shape.
 *  - NO `refetchInterval` — this is explicitly ON-DEMAND, never a
 *    polling loop (the product rail's non-negotiable).
 *  - `retry: false` — a failed fetch already resolves gracefully to
 *    `upToDate: undefined` at the IPC layer (fail-open); retrying
 *    would only delay showing that "unknown" state.
 *
 * The `upToDate` field itself carries the fail-open contract:
 * `true` only on a POSITIVE confirmation that HEAD has nothing new to
 * send; `undefined` (offline, not-yet-checked, or any failure) must
 * leave the Deploy button enabled — see `deploy-action-state.ts`'s
 * `DeployReadinessSignals.upToDate` doc.
 */
export const DEPLOY_UP_TO_DATE_OPTIONS = {
  staleTime: 30_000,
  refetchOnWindowFocus: 'always',
  retry: false,
} as const;

export function useDeployUpToDate(
  gameId: string | undefined,
  opts?: { enabled?: boolean },
): {
  upToDate: boolean | undefined;
  isLoading: boolean;
} {
  const client = useCrucibleClient();
  const enabled = (opts?.enabled ?? true) && typeof gameId === 'string' && gameId.length > 0;
  const query = useQuery<DeployUpToDateSignal>({
    queryKey: deployUpToDateQueryKey(client, gameId),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('deploy:check-up-to-date', { gameId });
    },
    enabled,
    ...DEPLOY_UP_TO_DATE_OPTIONS,
  });
  // Bugbot round 3 — two related fail-open gaps in trusting `query.data`
  // directly:
  //
  //   1. "Later IPC rejection keeps returning the stale true" — React
  //      Query does NOT clear `data` when a later fetch errors (a
  //      background refetch failure leaves `status: 'success'` and the
  //      PREVIOUS data in place). Returning `query.data?.upToDate`
  //      unconditionally meant a confirmed `true` kept blocking Deploy
  //      even after we'd since failed to reconfirm it.
  //
  //   2. "Stale up-to-date blocks deploy" even when the localGit-change
  //      invalidation (Workspace.tsx) never fires — e.g. `game:status`
  //      simply never refetches in that window. Relying SOLELY on an
  //      external invalidation trigger firing is not a safety net.
  //
  // Both close the same way: only trust the verdict while it is BOTH
  // free of a fetch error AND fresh (`!query.isStale` — React Query's
  // own staleTime-vs-`dataUpdatedAt` check, recomputed reactively as
  // time passes even with no active refetch). Once either condition
  // fails, this degrades to `undefined` ("unknown"), which the
  // readiness classifier never blocks on. This BOUNDS the worst case
  // to at most `staleTime` (30s) of a stale positive confirmation,
  // instead of an unbounded wait for a refetch trigger that might not
  // fire at all.
  // Bugbot — `query.isStale` guarantees the fail-open bound ONLY if the
  // component re-renders after the data ages fresh→stale, but React
  // Query does NOT re-render observers on that transition by itself.
  // With no other render (focus, invalidation, a status/chat update) a
  // cached `upToDate: true` could keep blocking Deploy past the claimed
  // 30s ceiling. Schedule a single one-shot re-render at the stale
  // boundary so the gate below is re-evaluated on time. This is NOT a
  // poll: it fires once per fresh positive verdict, forces a RE-RENDER
  // (not a refetch), and on that render the verdict degrades to
  // `undefined` (fail-open) — the next real trigger does the refetch.
  const [, forceReevaluate] = useReducer((n: number) => n + 1, 0);
  const dataUpdatedAt = query.dataUpdatedAt;
  const isPositive = !query.isError && query.data?.upToDate === true;
  useEffect(() => {
    if (!isPositive) return;
    const remaining = DEPLOY_UP_TO_DATE_OPTIONS.staleTime - (Date.now() - dataUpdatedAt);
    if (remaining <= 0) return; // already stale — the gate handles it this render
    const timer = setTimeout(forceReevaluate, remaining);
    return () => clearTimeout(timer);
  }, [isPositive, dataUpdatedAt]);

  const upToDate = query.isError || query.isStale ? undefined : query.data?.upToDate;
  return {
    upToDate,
    isLoading: query.isLoading,
  };
}
